import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import uniq from "lodash/uniq";

import { isValidHexCode } from "@stadium/helpers";

import { getMasterVariant as getProductMasterVariant } from "@helpers/products/search";
import { isSwagProduct } from "@helpers/products/types";

import {
  byo,
  giftCard,
  OPTIONTYPE_COLOR_KEY,
  OPTIONTYPE_PACK_KEY,
  OPTIONTYPE_PRICE_KEY,
  OPTIONTYPE_SIZE_KEY,
  OPTIONTYPE_ONLY_SIZE_KEY,
} from "@constants/products/detail";

const optionTypesOrder = [
  OPTIONTYPE_COLOR_KEY,
  ...OPTIONTYPE_SIZE_KEY,
  OPTIONTYPE_PACK_KEY,
  OPTIONTYPE_PRICE_KEY,
];

export const getVariantsExcludingMaster = (variantsIncludingMaster) =>
  variantsIncludingMaster.filter((variant) => !variant.isMaster);

export const getDefaultMultipleVariantIndex = ({
  variantValues = [],
  cartAddedVariant,
  isPricelessShop,
  intent,
}) => {
  if (isEmpty(variantValues)) {
    return -1;
  }

  if (cartAddedVariant?.productId != null) {
    const cartVariantIndex = variantValues.findIndex(
      (variant) => variant.externalId.toString() === cartAddedVariant.productId?.toString(),
    );
    if (cartVariantIndex >= 0) {
      return cartVariantIndex;
    }
  }

  if (isPricelessShop && !isSwagProduct(intent)) {
    return variantValues.length - 1;
  }

  return 0;
};

export const getMinPriceVariant = (variantsIncludingMaster) =>
  getVariantsExcludingMaster(variantsIncludingMaster).sort(
    (a, b) => Math.min(a.prices) - Math.min(b.prices),
  )[0];

const getRequiredOptionValues = (optionValue) =>
  optionTypesOrder.includes(optionValue.optionTypeKey);

export const getProductImageAlt = ({ name = "", color = "" } = {}) => {
  const colorSuffix = color ? ` – ${color}` : "";
  return `${name}${colorSuffix}`;
};

export const processVariantImages = (images) =>
  images?.map((image, index) => {
    return { id: `image${index + 1}`, image: image };
  });

const getVariantImages = (variant, masterVariant) => {
  if (!isEmpty(variant?.images)) return variant.images;
  if (!isEmpty(masterVariant?.images)) return [masterVariant.images[0]];
  if (!isEmpty(variant?.catalogVariants?.[0]?.images)) return variant?.catalogVariants?.[0]?.images;
  return [];
};

export const getProductOptionTypesData = (product) => {
  const { optionTypes = [], variantsIncludingMaster = [] } = product || {};
  const variantsExcludingMaster = getVariantsExcludingMaster(variantsIncludingMaster);
  const masterVariant = getMasterVariant(product);
  const optionsTypesHash = groupBy(optionTypes, "id");
  const optionValues = variantsExcludingMaster.reduce((acc, variant) => {
    const image = !isEmpty(variant.images) ? variant.images[0] : masterVariant.images?.[0];
    const unprocessedImages = getVariantImages(variant, masterVariant);
    const variantImages = processVariantImages(unprocessedImages);

    variant.optionValues.forEach((optionVal) => {
      if (optionsTypesHash[optionVal.optionTypeId]) {
        const optionTypeKey = optionsTypesHash[optionVal.optionTypeId][0].key;
        const optionTypePresentation = optionsTypesHash[optionVal.optionTypeId][0].presentation;
        acc.push({
          ...optionVal,
          variantId: variant.id,
          vendorId: variant.vendorByCountry?.externalId,
          amountIn: variant.amountIn,
          externalId: variant.externalId,
          priceIn: variant.priceIn,
          minOrderableQuantity: variant.minOrderableQuantity,
          weight: variant.weight,
          measurementUnit: variant.measurementUnit,
          image,
          images: variantImages,
          optionTypeKey,
          optionTypePresentation,
          hasTierPricing: variant.hasTierPricing,
          previewModeOptionvalue: [
            { ...optionVal, optionType: optionsTypesHash[optionVal.optionTypeId][0] },
          ],
          outOfStock: variant.outOfStock || !variant?.vendorByCountry?.externalId,
        });
      }
    });
    return acc;
  }, []);

  const optionValuesData = optionValues.filter(getRequiredOptionValues).reduce((acc, optionVal) => {
    const optType = optionsTypesHash[optionVal.optionTypeId][0];

    if (optType) {
      const key = OPTIONTYPE_SIZE_KEY.includes(optType.key) ? "size" : optType.key;
      acc[key] = [...(acc[key] || []), optionVal];
    }
    return acc;
  }, {});

  return optionValuesData;
};

const isImageAvailable = (image) => image && image !== null;

export const getProductVariantsData = (optionTypesData) => {
  const optionTypes = [];

  optionTypesOrder.forEach((optType) => {
    if (optionTypesData[optType]) {
      optionTypes.push(optType);
    }
  });

  // const optionTypes = Object.keys(optionTypesData);
  if (isEmpty(optionTypes)) return {};

  const variantsData = {};

  orderBy(optionTypesData[optionTypes[0]], "position").forEach((optionVal) => {
    let secondOptionTypeVariants = [];
    if (optionTypes.length > 1)
      secondOptionTypeVariants = optionTypesData[optionTypes[1]].filter(
        (opt) => opt.variantId === optionVal.variantId,
      );

    //add optionValue of parent in subvariant
    secondOptionTypeVariants.forEach((variant) => {
      variant.previewModeOptionvalue.push(...(optionVal.previewModeOptionvalue || []));
    });

    const { id, image } = optionVal;
    variantsData[id] = {
      ...optionVal,
      image: isImageAvailable(variantsData[id]?.image) ? variantsData[id]?.image : image,
      variants: [...(variantsData[id]?.variants || []), ...secondOptionTypeVariants],
    };

    Object.keys(variantsData).forEach((variant, index) => {
      variantsData[variant].index = index;
    });
  });

  Object.keys(variantsData).forEach((key) => {
    variantsData[key].variants = orderBy(variantsData[key].variants, "position");
  });

  return variantsData;
};

export const getProductSliderImages = (product = {}) => {
  const sliderImages = product?.variantsIncludingMaster.reduce((acc, variant) => {
    const variantId = variant.id;
    variant.images.forEach((image) => {
      acc.push({ imageId: acc.length, url: image, variantId });
    });
    return acc;
  }, []);

  if (!sliderImages?.length) {
    sliderImages?.push({ imageId: "no-image", url: null });
  }
  return sliderImages;
};

export const getMasterVariant = (product = {}) => {
  const masterVariant = product.variantsIncludingMaster?.find((variant) => variant.isMaster) || {};
  const image = masterVariant?.images?.[0] || product?.variantImages?.[0];
  if (image) return { ...masterVariant, image };
  return masterVariant;
};

const excludedIntentForShippingDetails = [giftCard, byo];
export const isShippingDetailRequired = (intent) =>
  !excludedIntentForShippingDetails.includes(intent);

export const orderbyOptionTypes = (optionTypesData, key) =>
  Object.keys(optionTypesData).reduce((acc, optType) => {
    acc[optType] = orderBy(optionTypesData[optType], key, "asc");
    return acc;
  }, {});

const getDiscountPecentage = (firstVariantPrice, currentVariantPrice) => {
  // Round to avoid floating-point precision issues (e.g., 16.40 - 15.58 = 0.8199999999999985)
  const priceDifference = Math.round((firstVariantPrice - currentVariantPrice) * 100) / 100;
  return Math.floor((priceDifference * 100) / firstVariantPrice);
};

const processedDiscountTableData = (prices) => {
  if (prices?.length) {
    const firstRangeAmount = prices[0]?.priceIn;
    return prices.map((price) => {
      return {
        range: { min: price.min, max: price.max },
        amount: price.priceIn,
        discount: getDiscountPecentage(firstRangeAmount, price?.priceIn),
        id: price.id,
      };
    });
  }

  return prices;
};

export const getVariantDiscountTableData = ({ masterVariant }) => {
  return processedDiscountTableData(masterVariant.prices);
};

export const getQuantityFromSizeMapping = (mapping = {}) =>
  Object.values(mapping).reduce((acc, current) => {
    acc = acc + parseInt(current.qty || 0);
    return acc;
  }, 0);

export const getTotalFromSizeMapping = (mapping = {}, { ceilRequired = false } = {}) =>
  Object.values(mapping).reduce(
    (a, b) =>
      a +
      parseFloat(
        (b.qty || 0) * ((ceilRequired ? Math.ceil(parseFloat(b.price).toFixed(4)) : b.price) || 0),
      ),
    0,
  );

export const getMinPriceForProduct = (prices, total, quantity) => {
  if (quantity === 0) {
    return Math.min(...prices);
  }
  return (total / quantity).toFixed(2);
};

export const getProductOptionValueId = (product, type = OPTIONTYPE_ONLY_SIZE_KEY) =>
  product.optionTypes?.find(({ key }) => key === type) || {};

export const getProductColorsAndSizes = (product) => {
  if (!product) {
    return [];
  }
  const colorValueId = getProductOptionValueId(product, OPTIONTYPE_COLOR_KEY)?.id;
  const sizeValueId = getProductOptionValueId(product, OPTIONTYPE_ONLY_SIZE_KEY)?.id;

  const variantsExcludingMaster = getVariantsExcludingMaster(product?.variantsIncludingMaster);

  return variantsExcludingMaster?.reduce(
    (colorSizeMapping, variant) => {
      const { colorOptionsValue, sizeOptionsValue } = colorSizeMapping;

      const { optionValues } = variant;

      const colorOption = optionValues.find(({ optionTypeId }) => optionTypeId === colorValueId);

      const sizeOption = optionValues.find(({ optionTypeId }) => optionTypeId === sizeValueId);

      if (colorOption && !colorOptionsValue.find(({ id }) => id === colorOption.id)) {
        colorOptionsValue.push({ ...colorOption, variant });
      }

      if (sizeOption && !sizeOptionsValue.find(({ id }) => id === sizeOption.id)) {
        sizeOptionsValue.push({ ...sizeOption, variant });
      }

      return colorSizeMapping;
    },
    { colorOptionsValue: [], sizeOptionsValue: [] },
  );
};

export const getCanBuyProduct = ({ product }) => !product.outOfStock && product.purchasable;

export const getProductAllVariantImages = (variants = []) => {
  const masterVariant = getProductMasterVariant(variants);
  const variantsExcludingMaster = getVariantsExcludingMaster(variants);

  const images = variantsExcludingMaster.reduce((variantImages, { images }) => {
    variantImages.push(...(images || []));
    return variantImages;
  }, []);

  const uniqueImages = uniq(images);

  return uniqueImages?.length ? uniqueImages : masterVariant?.images;
};

export const getDefaultIndexIfSingleVariantAvailable = (variants, variantsData) => {
  // select color if only one color is in stock
  const primaryVariants = variants.filter((variant) => {
    // if no size variants, we will check for color is in stock
    if (isEmpty(variantsData[variant].variants)) {
      return !variantsData[variant].outOfStock;
    }
    // if size variants, atleast one size should be instock
    return variantsData[variant].variants.some(({ outOfStock }) => !outOfStock);
  });
  // if only single variant found we will select it by default
  if (primaryVariants.length === 1) {
    return variants.findIndex((item) => item === primaryVariants[0]);
  }
  return -1;
};

export const sortInvalidColorLast = (a, b) => isValidHexCode(b?.name) - isValidHexCode(a?.name);
